import {SKIP_FIELDS} from '../constants/field-mappings';
import {TranslationFn} from '../types/common-types';
import {normalizeFilterValue} from './filter-normalizer';
import {isEqual} from './value-comparison';
import {getFieldLabel, translateValue} from './value-translator';

/**
 * Interface représentant un changement détecté entre deux valeurs
 */
export interface Change {
  before: unknown;
  after: unknown;
  type?: 'select' | 'text' | 'array' | 'boolean';
}

/**
 * Compare récursivement deux objets et détecte les changements
 * @param original - Objet original
 * @param current - Objet actuel
 * @param t - Fonction de traduction
 * @param path - Chemin du champ (pour le contexte)
 * @returns Dictionnaire des changements détectés
 */
export const deepDiff = (original: any, current: any, t: TranslationFn, path: string = ''): Record<string, Change> => {
  const changes: Record<string, Change> = {};

  // indicateurs fixes sans filtres, on force les filtres à une liste vide pour éviter de set tous les filtres par défaut et d'avoir une modal de modification avec tous les filtres ajoutés
  if (original?.FixedIndicatorId !== 0 && current?.FixedIndicatorId !== 0 && original?.filters?.length === 0) {
    current.filters = [];
  }

  // Pas de changement détecté
  if (isEqual(original, current, path)) return changes;

  // Normalisation des filtres manquants
  const normalizedOriginal = normalizeFilterValue(original, current, path);

  // Ignorer certains champs (qu'on ne veut pas voir dans la modal, exemple updatedAt)
  if (SKIP_FIELDS.some((field) => path.endsWith(field))) return changes;

  // objet ou tableaux
  if (
    typeof normalizedOriginal !== 'object' ||
    typeof current !== 'object' ||
    Array.isArray(normalizedOriginal) ||
    Array.isArray(current) ||
    normalizedOriginal == null ||
    current == null
  ) {
    if (path && !isEqual(normalizedOriginal, current, path)) {
      const label = getFieldLabel(path, t);
      changes[label] = {
        before: translateValue(normalizedOriginal, path, t),
        after: translateValue(current, path, t),
      };
    }
    return changes;
  }

  // Cas récursif : objets imbriqués
  const allKeys = new Set([...Object.keys(normalizedOriginal || {}), ...Object.keys(current || {})]);

  allKeys.forEach((key) => {
    const newPath = path ? `${path}.${key}` : key;
    const nestedChanges = deepDiff(normalizedOriginal?.[key], current?.[key], t, newPath);
    Object.assign(changes, nestedChanges);
  });

  return changes;
};
